import type { PostHog } from 'posthog-js'
import type { Router } from 'vue-router'
import {
  getPosthogApiKey,
  getPosthogDeploymentId,
  getPosthogEnabledClientIds,
  getPosthogEnvironmentTier,
  getPosthogHost,
  getPosthogPseudonymPepper
} from '@/global/environmentGetters'
import { AuthenticationService } from '@/services/AuthenticationService'
import { useUserStore } from '@/stores/user'
import { telemetryEvents } from '@/telemetry/eventNames'
import { EventQueue, type QueuedEvent } from '@/telemetry/eventQueue'
import { isClientAllowed } from '@/telemetry/clientAllowlist'
import { createPageviewHook } from '@/telemetry/pageviewHook'
import { derivePseudonym } from '@/telemetry/pseudonym'
import { sanitizeProperties } from '@/telemetry/sanitizeProperties'

const QUEUE_LIMIT = 50
// The organisational unit for analytics is the tenant. It is 1:1 with the user, comes
// from the token, and is known before the SDK loads - unlike the hospital list, which
// arrives after mount and can hold several entries.
const CLIENT_GROUP_TYPE = 'client'

let enabled = false
let client: PostHog | undefined
let sdkReady = false
let sessionId = ''
let sessionStartedAt = 0
let sessionEnded = false
let beaconMode = false
let clientId = ''
const queue = new EventQueue(QUEUE_LIMIT)

export function startTelemetry(router: Router): void {
  const allowlist = getPosthogEnabledClientIds()
  if (allowlist.length === 0) {
    return
  }

  const tenant = AuthenticationService.getClientId()
  if (!isClientAllowed(tenant, allowlist)) {
    // Only reachable once someone has deliberately configured an allowlist, so this
    // is an onboarding aid rather than noise: it names the id that would need adding.
    console.info(
      `[telemetry] not collecting - client_id ${tenant ?? '(absent)'} is not in POSTHOG_ENABLED_CLIENT_IDS`
    )
    return
  }

  const apiKey = getPosthogApiKey()
  if (!apiKey) {
    console.warn(
      '[telemetry] a tenant is allowlisted but POSTHOG_API_KEY is missing - staying disabled'
    )
    return
  }

  clientId = tenant!.trim().toLowerCase()
  enabled = true
  sessionId = crypto.randomUUID()
  sessionStartedAt = Date.now()

  router.afterEach(createPageviewHook(captureEvent))
  window.addEventListener('pagehide', onPageHide)

  captureEvent(telemetryEvents.SESSION_STARTED, {})

  void loadSdk(apiKey)
}

// Events are held only until the dynamically imported SDK is ready - a few hundred
// milliseconds at most - so the initial navigation and session.started are not lost.
// The one exception is a logout inside that window: keycloak-js redirects synchronously,
// so the queue is never flushed and the sub-second session goes unreported. Accepted.
export function captureEvent(name: string, properties: Record<string, unknown>): void {
  if (!enabled) {
    return
  }

  const withSession = { ...properties, session_id: sessionId }

  if (!sdkReady) {
    queue.push({ name, properties: withSession, timestamp: new Date() })
    return
  }

  send({ name, properties: withSession, timestamp: new Date() })
}

export function endSession(reason: 'logout' | 'page_unloaded'): void {
  if (!enabled || sessionEnded) {
    return
  }
  sessionEnded = true

  // Never reset: a full-page unload follows every call (Keycloak redirect or pagehide),
  // so this module lives for exactly one session. A future in-app flow that ends a
  // session without unloading would need to reset this and sessionEnded.
  beaconMode = true

  captureEvent(telemetryEvents.SESSION_ENDED, {
    duration_ms: Date.now() - sessionStartedAt,
    end_reason: reason
  })
}

export function resetTelemetry(): void {
  if (!enabled) {
    return
  }
  client?.reset(true)
}

async function loadSdk(apiKey: string): Promise<void> {
  try {
    const { default: posthog } = await import('posthog-js')

    const subject = AuthenticationService.getUserId()
    if (!subject) {
      console.warn('[telemetry] no subject claim available - staying disabled')
      enabled = false
      return
    }

    const pepper = getPosthogPseudonymPepper()
    if (!pepper) {
      console.warn('[telemetry] POSTHOG_PSEUDONYM_PEPPER is missing - staying disabled')
      enabled = false
      return
    }

    // The Keycloak subject never reaches PostHog. Only this keyed derivative does, and
    // without the pepper it cannot be joined back to the subject.
    const distinctId = await derivePseudonym(subject, pepper)

    posthog.init(apiKey, {
      api_host: getPosthogHost(),
      autocapture: false,
      capture_pageview: false,
      capture_pageleave: false,
      capture_performance: false,
      capture_heatmaps: false,
      rageclick: false,
      save_campaign_params: false,
      disable_session_recording: true,
      disable_surveys: true,
      person_profiles: 'identified_only',
      // No cookie and no localStorage: logout() clears localStorage but not cookies,
      // so a persisted distinct_id would leak into the next user's session on a
      // shared workstation. It also keeps the client free of terminal-equipment storage.
      persistence: 'memory',
      // Required alongside memory persistence. Without it PostHog mints a fresh
      // anonymous id on every page load and identify() merges each one onto the
      // person, which eventually exceeds the per-person distinct-id limit and drops
      // that person off the person and session views. The pseudonym is stable, so
      // bootstrapping with it means no anonymous id is ever minted.
      bootstrap: { distinctID: distinctId, isIdentifiedID: true },
      // Otherwise project settings could remotely re-enable session recording,
      // heatmaps and exception capture. (advanced_disable_decide is the deprecated
      // spelling of this and warns at runtime.)
      advanced_disable_flags: true,
      property_denylist: ['$current_url', '$pathname', '$host', '$referrer', '$referring_domain'],
      before_send: (event) =>
        event && { ...event, properties: sanitizeProperties(event.properties) }
    })

    posthog.register({
      client_id: clientId,
      environment_tier: getPosthogEnvironmentTier(),
      deployment_id: getPosthogDeploymentId()
    })

    // Set before any event is sent, so every flushed event carries $groups.client.
    posthog.group(CLIENT_GROUP_TYPE, clientId)

    const user = useUserStore().user
    posthog.identify(distinctId, {
      role: user?.currentRole ?? 'unknown',
      user_rights: user?.userRights ?? []
    })

    client = posthog
    sdkReady = true
    flushQueue()
  } catch (error) {
    console.warn('[telemetry] failed to initialise:', error)
    enabled = false
  }
}

function flushQueue(): void {
  for (const event of queue.drain()) {
    send(event)
  }

  if (queue.droppedCount > 0) {
    console.warn(`[telemetry] dropped ${queue.droppedCount} queued events before flushing`)
  }
}

function send(event: QueuedEvent): void {
  client?.capture(event.name, event.properties, {
    timestamp: event.timestamp,
    ...(beaconMode ? { transport: 'sendBeacon' as const, send_instantly: true } : {})
  })
}

function onPageHide(pageTransitionEvent: PageTransitionEvent): void {
  // A bfcache entry is not the end of the session.
  if (pageTransitionEvent.persisted) {
    return
  }
  endSession('page_unloaded')
}
